export interface UserFeatureRef {
  source: 'user'
  featureId: string
}

export interface CotrexTrailRef {
  source: 'cotrex-trail'
  name: string
  placeId: number
  featureId: string
}

export interface CotrexTrailheadRef {
  source: 'cotrex-trailhead'
  name: string
  coordinates: [number, number]
}

export interface BackcountryTrailRef {
  source: 'backcountry-trail'
  name: string
  // OSM trail geometry captured at selection time. OSM tiles carry no stable id
  // and the geometry layer carries no name, so we keep the geometry itself — it
  // drives the highlight and (later) stitches into plan routes.
  geometry: GeoJSON.Geometry
}

export interface BackcountryPOIRef {
  source: 'backcountry-poi'
  name: string
  subclass: string
  coordinates: [number, number]
  geometry: GeoJSON.Geometry
}

export interface CotrexClosureRef {
  source: 'cotrex-closure'
  name: string
}

export interface FireFeatureRef {
  source: 'fire'
  fireType: string
  name: string
}

export interface SearchResultRef {
  source: 'search'
  searchId: string
}

export type SelectableFeatureRef =
  | UserFeatureRef
  | CotrexTrailRef
  | CotrexTrailheadRef
  | CotrexClosureRef
  | FireFeatureRef
  | BackcountryTrailRef
  | BackcountryPOIRef
  | SearchResultRef

/** A stable-ish key for a captured geometry: endpoints + vertex count, rounded.
 *  Enough to dedupe the same tile-clipped piece without merging distinct ones. */
function geometrySignature(g: GeoJSON.Geometry): string {
  const r = (n: number): string => n.toFixed(5)
  const pt = (c: number[]): string => `${r(c[0])},${r(c[1])}`
  switch (g.type) {
    case 'Point':
      return pt(g.coordinates)
    case 'LineString': {
      const cs = g.coordinates
      return cs.length ? `${pt(cs[0])}~${pt(cs[cs.length - 1])}~${cs.length}` : 'empty'
    }
    case 'MultiLineString': {
      const lines = g.coordinates
      const first = lines[0]?.[0]
      const lastLine = lines[lines.length - 1]
      const last = lastLine?.[lastLine.length - 1]
      const n = lines.reduce((sum, l) => sum + l.length, 0)
      return `${first ? pt(first) : '?'}~${last ? pt(last) : '?'}~${n}`
    }
    default:
      return JSON.stringify('bbox' in g ? g.bbox : g).slice(0, 48)
  }
}

export function featureRefKey(ref: SelectableFeatureRef): string {
  switch (ref.source) {
    case 'user':
      return `user:${ref.featureId}`
    case 'cotrex-trail':
      if (ref.placeId) return `cotrex-trail:p${ref.placeId}`
      if (ref.featureId) return `cotrex-trail:f${ref.featureId}`
      return `cotrex-trail:n${ref.name}`
    case 'cotrex-trailhead':
      return `cotrex-trailhead:${ref.name}:${ref.coordinates[0]}:${ref.coordinates[1]}`
    case 'cotrex-closure':
      return `cotrex-closure:${ref.name}`
    case 'fire':
      return `fire:${ref.fireType}:${ref.name}`
    case 'backcountry-trail':
      // Name is often empty and the same trail splits across tiles, so key on a
      // geometry signature (endpoints + vertex count) to dedupe identical pieces.
      return `backcountry-trail:${geometrySignature(ref.geometry)}`
    case 'backcountry-poi':
      return `backcountry-poi:${ref.subclass}:${ref.coordinates[0]}:${ref.coordinates[1]}`
    case 'search':
      return `search:${ref.searchId}`
  }
}
